import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Calendar, XCircle, Video, RefreshCw } from "lucide-react";

const API = "/api";

export default function TrialBookings() {
  const { getAuthHeader } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBookings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/trial/bookings`, { headers: getAuthHeader() });
      setBookings(res.data.items || []);
    } catch (err) {
      toast.error("Failed to load trial bookings");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const handleCancel = async (id, name) => {
    try {
      await axios.put(`${API}/trial/bookings/${id}/cancel`, {}, { headers: getAuthHeader() });
      toast.success(`Trial booking for ${name} cancelled`);
      fetchBookings();
    } catch (err) {
      toast.error("Failed to cancel");
    }
  };

  const formatTime12 = (t24) => {
    if (!t24) return "-";
    const [h, m] = t24.split(":");
    const hour = parseInt(h);
    return `${hour > 12 ? hour - 12 : hour}:${m} ${hour >= 12 ? "PM" : "AM"}`;
  };

  const confirmed = bookings.filter(b => b.status === "confirmed");
  const cancelled = bookings.filter(b => b.status === "cancelled");

  return (
    <div className="space-y-6" data-testid="trial-bookings-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-pink-100">
            <Calendar className="w-6 h-6 text-pink-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Trial Bookings</h1>
            <p className="text-sm text-slate-500">{confirmed.length} active, {cancelled.length} cancelled</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchBookings} size="sm">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Active Trial Bookings</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading...</div>
          ) : confirmed.length === 0 ? (
            <div className="text-center py-8 text-slate-400">No active trial bookings</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Student</TableHead>
                  <TableHead className="font-semibold">Tutor</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold">Time</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">WhatsApp</TableHead>
                  <TableHead className="font-semibold">Level</TableHead>
                  <TableHead className="font-semibold text-center">Zoom</TableHead>
                  <TableHead className="text-right font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {confirmed.map(b => (
                  <TableRow key={b.id}>
                    <TableCell className="font-medium">{b.student_name}</TableCell>
                    <TableCell>{b.tutor_name}</TableCell>
                    <TableCell>{b.date}</TableCell>
                    <TableCell>{formatTime12(b.time)}</TableCell>
                    <TableCell className="text-sm text-slate-500">{b.email}</TableCell>
                    <TableCell className="text-sm text-slate-500">{b.whatsapp}</TableCell>
                    <TableCell><span className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded text-xs font-medium">{b.english_level}</span></TableCell>
                    <TableCell className="text-center">
                      <a href={b.zoom_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                        <Video className="w-4 h-4 inline" />
                      </a>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => handleCancel(b.id, b.student_name)}
                        className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`cancel-trial-${b.id}`}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Cancel
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
